import { db } from '../../../context/Firebase';
import { collection, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc, query, orderBy, startAfter, limit, getCountFromServer, where, setDoc } from 'firebase/firestore';
import PostRepository from '../../../domain/repositories/PostRepository';
import Post from '../../../domain/entities/Post';

class FirebasePostRepository extends PostRepository {
    constructor() {
        super();
        this.collectionRef = collection(db, 'posts');
    }

    /**
     * Converts a Firestore document snapshot to a Post entity.
     * @param {DocumentSnapshot} docSnap - The Firestore document snapshot.
     * @returns {Post}
     */
    _toPostEntity(docSnap) {
        const data = docSnap.data();
        return new Post({
            id: docSnap.id,
            userId: data.userId,
            userName: data.userName,
            userProfilePic: data.userProfilePic,
            content: data.content || data.post || data.description, // Handle inconsistent data model
            imageUrl: data.imageUrl,
            createdAt: data.createdAt.toDate(), // Convert Firebase Timestamp to Date object
            likes: data.likes || [],
            commentsCount: data.commentsCount || 0,
        });
    }

    async getPosts(limitCount, startAfterId) {
        let q = query(this.collectionRef, orderBy('createdAt', 'desc'));

        if (startAfterId) {
            const startAfterDoc = await getDoc(doc(db, 'posts', startAfterId));
            if (startAfterDoc.exists()) {
                q = query(q, startAfter(startAfterDoc));
            }
        }

        if (limitCount) {
            q = query(q, limit(limitCount));
        }

        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(this._toPostEntity);
    }

    async getPostById(id) {
        const docRef = doc(db, 'posts', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return this._toPostEntity(docSnap);
        }
        return null;
    }

    /**
     * Extract hashtags from content
     * @param {string} content - The post content
     * @returns {Array<string>} - Array of hashtags without # symbol
     */
    _extractHashtags(content) {
        if (!content) return [];
        const hashtagRegex = /#(\w+)/g;
        const matches = content.match(hashtagRegex);
        return matches ? matches.map(tag => tag.substring(1).toLowerCase()) : [];
    }

    /**
     * Update hashtag collection with post reference
     * @param {Array<string>} hashtags - Array of hashtag strings
     * @param {string} postId - The post ID
     */
    async _updateHashtags(hashtags, postId) {
        const hashtagsRef = collection(db, 'hashtags');
        for (const tag of hashtags) {
            const tagDocRef = doc(hashtagsRef, tag);
            const tagDoc = await getDoc(tagDocRef);
            
            if (tagDoc.exists()) {
                // Update existing hashtag
                const currentPosts = tagDoc.data().posts || [];
                if (!currentPosts.includes(postId)) {
                    await updateDoc(tagDocRef, {
                        posts: [...currentPosts, postId],
                        count: (tagDoc.data().count || 0) + 1,
                        lastUsed: new Date()
                    });
                }
            } else {
                // Create new hashtag with setDoc to use tag as document ID
                await setDoc(tagDocRef, {
                    tag: tag,
                    posts: [postId],
                    count: 1,
                    createdAt: new Date(),
                    lastUsed: new Date()
                });
            }
        }
    }

    async createPost(post) {
        let imageUrl = null;
        if (post.imageFile) {
            // Enforce 750 KB limit before encoding — base64 is ~33% larger than the raw
            // file, so anything bigger risks exceeding Firestore's 1 MiB document limit.
            const MAX_SIZE = 750 * 1024;
            if (post.imageFile.size > MAX_SIZE) {
                throw new Error("Post image must be smaller than 750 KB.");
            }
            imageUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(post.imageFile);
            });
        }

        // Extract hashtags from content
        const hashtags = this._extractHashtags(post.content);

        const newPostData = {
            userId: post.userId,
            userName: post.userName,
            userProfilePic: post.userProfilePic,
            content: post.content,
            imageUrl: imageUrl,
            createdAt: new Date(),
            likes: [],
            commentsCount: 0,
            hashtags: hashtags,
        };
        const docRef = await addDoc(this.collectionRef, newPostData);
        
        // Update hashtags collection
        if (hashtags.length > 0) {
            await this._updateHashtags(hashtags, docRef.id);
        }
        
        return new Post({ ...newPostData, id: docRef.id });
    }

    async updatePost(id, updates) {
        const docRef = doc(db, 'posts', id);
        await updateDoc(docRef, updates);
    }

    async deletePost(id) {
        const docRef = doc(db, 'posts', id);
        await deleteDoc(docRef);
    }

    async likePost(postId, userId) {
        const postRef = doc(db, 'posts', postId);
        const postSnap = await getDoc(postRef);
        if (postSnap.exists()) {
            const postData = postSnap.data();
            const currentLikes = postData.likes || [];
            if (!currentLikes.includes(userId)) {
                await updateDoc(postRef, {
                    likes: [...currentLikes, userId]
                });
            }
        }
    }

    async unlikePost(postId, userId) {
        const postRef = doc(db, 'posts', postId);
        const postSnap = await getDoc(postRef);
        if (postSnap.exists()) {
            const postData = postSnap.data();
            const currentLikes = postData.likes || [];
            await updateDoc(postRef, {
                likes: currentLikes.filter(id => id !== userId)
            });
        }
    }

    async getCommentCountForPost(postId) {
        try {
            const commentRef = collection(db, "posts", postId, "comments");
            const snapshot = await getCountFromServer(commentRef);
            return snapshot.data().count;
        } catch (err) {
            console.error(`Error fetching comment count for post ${postId}:`, err);
            return 0;
        }
    }

    /**
     * Get posts by hashtag
     * @param {string} hashtag - The hashtag to filter by (without #)
     * @returns {Promise<Array<Post>>}
     */
    async getPostsByHashtag(hashtag) {
        const q = query(
            this.collectionRef,
            where('hashtags', 'array-contains', hashtag.toLowerCase()),
            orderBy('createdAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(this._toPostEntity);
    }

    /**
     * Get trending hashtags
     * @param {number} limitCount - Number of hashtags to return
     * @returns {Promise<Array>}
     */
    async getTrendingHashtags(limitCount = 10) {
        const hashtagsRef = collection(db, 'hashtags');
        const q = query(hashtagsRef, orderBy('count', 'desc'), limit(limitCount));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    }
}

export default FirebasePostRepository;
